import React, { useMemo } from "react";

import { CustomRenderer as CustomRendererComponent } from "heat-next";

import { useSessionDetailsContext } from "@/context/session-details/session-details";

import EmptyState from "@/components/empty-state/empty-state";
import { EmptyStateType } from "@/components/empty-state/empty-state.types";

import { ComponentWrapper } from "@/pages/shared/shared.styles";

function findLayoutColConfiguration(
    gridLayoutData: unknown,
    componentId: string,
): Record<string, unknown> | undefined {
    if (!Array.isArray(gridLayoutData)) return undefined;

    for (const row of gridLayoutData) {
        const cols = (row as { cols?: unknown[] })?.cols;
        if (!Array.isArray(cols)) continue;

        for (const col of cols) {
            const c = col as { component?: string; configuration?: Record<string, unknown> };
            if (c.component === componentId) {
                return c.configuration;
            }
        }
    }

    return undefined;
}

const CustomRenderer: React.FC = () => {
    const { gridLayoutData } = useSessionDetailsContext();

    const { item, title, tooltip } = useMemo(() => {
        const configuration = findLayoutColConfiguration(gridLayoutData, "CustomRenderer");
        const customRendererItem = configuration?.customRendererItem as
            | {
                  html?: string;
                  css?: string;
                  js?: string;
                  useCard?: boolean;
                  renderMode?: "sandbox" | "inline";
                  height?: string;
              }
            | undefined;

        return {
            item: customRendererItem,
            title: (configuration?.titleContent as string) ?? "",
            tooltip:
                configuration?.shouldRenderTooltip === false
                    ? ""
                    : ((configuration?.tooltipContent as string) ?? ""),
        };
    }, [gridLayoutData]);

    if (!item?.html) {
        return (
            <ComponentWrapper>
                <EmptyState
                    title="session-details:no-data:empty-state:title"
                    emptyStateType={EmptyStateType.Charts}
                    minHeight="12rem"
                />
            </ComponentWrapper>
        );
    }

    return (
        <CustomRendererComponent
            html={item.html}
            css={item.css}
            js={item.js}
            useCard={item.useCard ?? true}
            renderMode={item.renderMode ?? "sandbox"}
            title={title}
            tooltip={tooltip}
            height={item.height}
        />
    );
};

export default CustomRenderer;
